/**
 * The consulting firm's screen.
 *
 * Two audiences meet here and the boundary between them is the whole point. The roster
 * is the firm's own book and names the firm's own clients. The benchmark below it is
 * what a client is shown, and every peer figure on it is an aggregate — no other
 * client's performance is recoverable from anything on this page.
 *
 * Gold stays what Actuovious computed. The network band is neutral because it is
 * context rather than a competing claim, and rust marks the contractual target and a
 * position behind the network: the things worth acting on.
 */

import { useEffect, useState } from "react";
import { Notice, Section, StatTile } from "../components/Chrome";
import { fetchBenchmarks, fetchNetworkConsole } from "../lib/api";
import { money, moneyCompact, periodLabel, recordCount } from "../lib/format";
import type { BenchmarkMetric, BenchmarksResponse, NetworkConsole } from "../lib/types";

/**
 * The value range the bar is drawn over.
 *
 * Computed per metric rather than fixed at 0–100: availability lives between 99.70 and
 * 99.85, and a full-width axis would render every client on top of every other.
 */
export function domainFor(metric: BenchmarkMetric): { lo: number; hi: number } {
  const points = [
    metric.your_value,
    metric.network_low,
    metric.network_high,
    metric.expected_target,
  ];
  const lo = Math.min(...points);
  const hi = Math.max(...points);
  const pad = hi - lo === 0 ? 0.5 : (hi - lo) * 0.18;
  return { lo: lo - pad, hi: hi + pad };
}

function BenchmarkBar({ metric }: { metric: BenchmarkMetric }) {
  const { lo, hi } = domainFor(metric);
  const width = 260;
  const height = 34;
  const at = (value: number) => ((value - lo) / (hi - lo)) * width;

  const behind = metric.position === "behind";
  const bandStart = at(metric.network_low);
  const bandWidth = Math.max(at(metric.network_high) - bandStart, 1.5);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={
        `${metric.name}: this client ${metric.your_value}${metric.unit}, ` +
        `the network band ${metric.network_low}${metric.unit} to ${metric.network_high}` +
        `${metric.unit} across ${metric.sites} clients, ${metric.position} the band`
      }
      style={{ display: "block", maxWidth: "100%" }}
    >
      <line x1={0} y1={17} x2={width} y2={17} stroke="var(--line-strong)" strokeWidth={1} />
      {/* The middle half of the network. Neutral: it is context, not a rival claim. */}
      <rect
        x={bandStart}
        y={11}
        width={bandWidth}
        height={12}
        fill="rgba(239,233,221,0.10)"
        stroke="rgba(239,233,221,0.32)"
        strokeWidth={1}
        rx={2}
      />
      <line
        x1={at(metric.expected_target)}
        y1={3}
        x2={at(metric.expected_target)}
        y2={31}
        stroke="var(--rust)"
        strokeWidth={2}
        strokeDasharray="4 3"
      />
      <circle
        cx={at(metric.your_value)}
        cy={17}
        r={5.5}
        fill="var(--gold)"
        stroke={behind ? "var(--rust)" : "var(--ink)"}
        strokeWidth={2}
      />
    </svg>
  );
}

function BenchmarkLegend() {
  const items = [
    { colour: "var(--gold)", label: "This client, computed" },
    { colour: "rgba(239,233,221,0.32)", label: "Network band, rounded" },
    { colour: "var(--rust)", label: "Contractual target" },
  ];
  return (
    <div style={{ display: "flex", gap: 18, flexWrap: "wrap", fontSize: 11, color: "var(--slate)" }}>
      {items.map((item) => (
        <span key={item.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              background: item.colour,
              display: "inline-block",
            }}
          />
          {item.label}
        </span>
      ))}
    </div>
  );
}

function BenchmarkTable({ benchmark }: { benchmark: BenchmarksResponse["benchmarks"][number] }) {
  return (
    <div className="card" style={{ padding: 18, marginBottom: 12 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 12,
        }}
      >
        <div>
          <div className="serif" style={{ fontSize: 17 }}>
            {benchmark.supplier_name}
          </div>
          <div style={{ fontSize: 12, color: "var(--slate)", marginTop: 3 }}>
            {benchmark.category} · measured at {benchmark.sites} clients ·{" "}
            {benchmark.peers} anonymous peers
          </div>
        </div>
        <BenchmarkLegend />
      </div>

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Service level</th>
              <th style={{ textAlign: "right" }}>This client</th>
              <th style={{ textAlign: "right" }}>Network band</th>
              <th style={{ textAlign: "right" }}>Position</th>
              <th style={{ minWidth: 280 }}>Against the network</th>
            </tr>
          </thead>
          <tbody>
            {benchmark.metrics.map((metric) => (
              <tr key={`${metric.metric_key}-${JSON.stringify(metric.dims)}`}>
                <td>
                  <div className="serif" style={{ fontSize: 14.5 }}>
                    {metric.name}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--slate)", marginTop: 3 }}>
                    target {metric.direction === "gte" ? "≥" : "≤"} {metric.expected_target}
                    {metric.unit} · {recordCount(metric.your_record_count)}
                  </div>
                </td>
                <td
                  className="num serif"
                  style={{ textAlign: "right", color: "var(--gold)", fontSize: 15 }}
                >
                  {metric.your_value}
                  {metric.unit}
                </td>
                <td className="num" style={{ textAlign: "right", color: "var(--parchment)" }}>
                  {metric.network_low}–{metric.network_high}
                  {metric.unit}
                  <div style={{ fontSize: 11, color: "var(--slate)" }}>
                    across {metric.sites} clients
                  </div>
                </td>
                <td
                  style={{
                    textAlign: "right",
                    textTransform: "capitalize",
                    color: metric.position === "behind" ? "var(--rust)" : "var(--parchment)",
                  }}
                >
                  {metric.position === "within" ? "Within band" : metric.position}
                  <div style={{ fontSize: 11, color: "var(--slate)" }}>
                    {metric.position === "within" ? "no outlier" : "of the network band"}
                  </div>
                </td>
                <td>
                  <BenchmarkBar metric={metric} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ConsoleView({ tenant }: { tenant: string | null }) {
  // The partner is attached to no single client, so nothing upstream can pick one. The
  // benchmark needs a subject, so the console chooses the first client on its own roster
  // rather than showing an empty panel.
  const [selected, setSelected] = useState<string | null>(tenant);
  const [data, setData] = useState<NetworkConsole | null>(null);
  const [benchmarks, setBenchmarks] = useState<BenchmarksResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      fetchNetworkConsole(),
      selected ? fetchBenchmarks(selected) : Promise.resolve(null),
    ])
      .then(([nextConsole, nextBenchmarks]) => {
        if (cancelled) return;
        setData(nextConsole);
        setBenchmarks(nextBenchmarks);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setError(caught instanceof Error ? caught.message : "Could not load the console.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selected]);

  // Once the roster arrives, adopt its first client so the benchmark has a subject.
  useEffect(() => {
    if (selected === null && data?.clients.length) {
      setSelected(data.clients[0].id);
    }
  }, [data, selected]);

  if (loading) {
    return <div style={{ color: "var(--slate)", fontSize: 13 }}>Reading every client…</div>;
  }
  if (error || !data) {
    return <Notice tone="warn">{error ?? "Could not load the console."}</Notice>;
  }

  const totals = data.totals;

  return (
    <div className="fade-in">
      <Section
        title="Network"
        subtitle={
          `Every client under management, as of ${periodLabel(data.as_of)}. Aggregates here ` +
          `span tenants; the benchmark below never lets one client see another.`
        }
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
          }}
        >
          <StatTile label="Clients" value={String(totals.clients)} />
          <StatTile
            label="Spend under management"
            value={moneyCompact(totals.annual_spend)}
            note={`Across ${totals.vendors} vendor engagements`}
          />
          <StatTile
            label="Credits identified"
            value={money(totals.credits_computed)}
            note={`Vendors concede ${money(totals.credits_reported)}`}
            tone="gold"
          />
          <StatTile
            label="Understated"
            value={money(totals.credits_understated)}
            note="Owed under contract and not claimed"
            tone="rust"
          />
          <StatTile
            label="Suppliers at 2+ clients"
            value={String(totals.shared_suppliers)}
            note={`Of ${totals.suppliers} suppliers tracked`}
          />
        </div>
      </Section>

      <Section
        title="Clients"
        subtitle="The firm's own book. This is the only place a client is named against a figure."
      >
        <div className="card table-scroll">
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th style={{ textAlign: "right" }}>Vendors</th>
                <th style={{ textAlign: "right" }}>Annual spend</th>
                <th style={{ textAlign: "right" }}>Credits identified</th>
                <th style={{ textAlign: "right" }}>Understated</th>
                <th style={{ textAlign: "right" }}>Material gaps</th>
              </tr>
            </thead>
            <tbody>
              {data.clients.map((client) => (
                <tr key={client.id}>
                  <td>
                    <div className="serif" style={{ fontSize: 14.5 }}>
                      {client.name}
                    </div>
                    <div style={{ fontSize: 11.5, color: "var(--slate)", marginTop: 3 }}>
                      {client.description}
                    </div>
                  </td>
                  <td className="num" style={{ textAlign: "right" }}>
                    {client.vendors}
                  </td>
                  <td className="num" style={{ textAlign: "right" }}>
                    {moneyCompact(client.annual_spend)}
                  </td>
                  <td
                    className="num serif"
                    style={{ textAlign: "right", color: "var(--gold)", fontSize: 15 }}
                  >
                    {money(client.credits_computed)}
                  </td>
                  <td
                    className="num"
                    style={{
                      textAlign: "right",
                      color: client.credits_understated > 0 ? "var(--rust)" : "var(--slate)",
                    }}
                  >
                    {money(client.credits_understated)}
                  </td>
                  <td className="num" style={{ textAlign: "right" }}>
                    {client.material_findings}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section
        title="Suppliers at more than one client"
        subtitle="Where the same supplier appears across the book. No client is named against a supplier here, because that is the fact the benchmark exists to withhold."
      >
        <div className="card table-scroll">
          <table>
            <thead>
              <tr>
                <th>Supplier</th>
                <th style={{ textAlign: "right" }}>Clients</th>
                <th style={{ textAlign: "right" }}>Combined spend</th>
                <th style={{ textAlign: "right" }}>Credits identified</th>
                <th>Benchmark</th>
              </tr>
            </thead>
            <tbody>
              {data.shared_suppliers.map((supplier) => (
                <tr key={supplier.supplier_id}>
                  <td>
                    <div className="serif" style={{ fontSize: 14.5 }}>
                      {supplier.supplier_name}
                    </div>
                    <div style={{ fontSize: 11.5, color: "var(--slate)", marginTop: 3 }}>
                      {supplier.category}
                    </div>
                  </td>
                  <td className="num" style={{ textAlign: "right" }}>
                    {supplier.clients}
                  </td>
                  <td className="num" style={{ textAlign: "right" }}>
                    {moneyCompact(supplier.annual_spend)}
                  </td>
                  <td
                    className="num serif"
                    style={{ textAlign: "right", color: "var(--gold)", fontSize: 15 }}
                  >
                    {money(supplier.credits_computed)}
                  </td>
                  <td style={{ fontSize: 12, color: "var(--slate)" }}>
                    {supplier.benchmarkable
                      ? "Published"
                      : `Withheld — under ${data.minimum_sites} clients`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {benchmarks && (
        <Section
          title={`Benchmark — ${benchmarks.tenant.name}`}
          subtitle={
            `How this client's independently computed performance compares to the same ` +
            `supplier's other engagements. Only service levels the platform measured itself ` +
            `enter a benchmark; a vendor's self-report is not evidence.`
          }
        >
          {benchmarks.benchmarks.map((benchmark) => (
            <BenchmarkTable key={benchmark.supplier_id} benchmark={benchmark} />
          ))}

          {benchmarks.benchmarks.length === 0 && (
            <Notice>
              No supplier of this client is tracked at enough others to publish a benchmark
              without exposing one of them.
            </Notice>
          )}

          {benchmarks.suppressed.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Notice>
                <strong style={{ color: "var(--parchment)" }}>Withheld</strong>
                <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                  {benchmarks.suppressed.map((row) => (
                    <li key={row.supplier_id} style={{ marginBottom: 4 }}>
                      <span style={{ color: "var(--parchment)" }}>{row.supplier_name}</span> is
                      tracked at {row.sites}{" "}
                      {row.sites === 1 ? "client" : "clients"}. A network figure over so few
                      would be one client's figure, so none is produced.
                    </li>
                  ))}
                </ul>
              </Notice>
            </div>
          )}
        </Section>
      )}
    </div>
  );
}
